/**
 * Blocking overlay shown on a wizard step while its generation/finalize
 * mutation is in flight (WizardContext's shared `loading` state). Render
 * it as the first child of a `relative`-positioned step root so it covers
 * exactly that step's content, and pass `loading` to the step's Back/
 * Continue buttons so they can't be clicked mid-request.
 *
 * Pass either a fixed `message`, or a `stages` array of messages to cycle
 * through on a timer (holding on the last stage until `active` goes
 * false) - used where the real call reliably takes long enough that a
 * single static "please wait" reads as stuck.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

// A hash-hit skip (spec P2 §4.2 - finalize-questions/generate-rfp/assess-risk
// short-circuiting on an unchanged input hash) resolves near-instantly, since
// no agent call happens at all. Delaying the overlay's appearance by this
// much means a skip never shows it - "Analyzing requirements..." or any
// other stage message flashing for an operation that did no real work would
// be actively misleading. A real generation takes seconds, so this delay is
// imperceptible there.
const SHOW_DELAY_MS = 400;

const GenerationLock = ({ active, message = 'Generating — please wait', stages, stageDurationMs = 8000 }) => {
  const [stageIndex, setStageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) {
      setStageIndex(0);
      setElapsedSeconds(0);
      setVisible(false);
      return;
    }

    const showTimer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);

    const startedAt = Date.now();
    const elapsedTimer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    let stageTimer;
    if (stages && stages.length > 1) {
      stageTimer = setInterval(() => {
        // Advance through stages but hold on the last one until the
        // response actually comes back (active flips false).
        setStageIndex((i) => Math.min(i + 1, stages.length - 1));
      }, stageDurationMs);
    }

    return () => {
      clearTimeout(showTimer);
      clearInterval(elapsedTimer);
      if (stageTimer) clearInterval(stageTimer);
    };
  }, [active, stages, stageDurationMs]);

  if (!active || !visible) return null;

  const displayMessage = stages && stages.length > 0 ? stages[stageIndex] : message;

  return (
    <>
      {/* Translucent backdrop - FIXED TO THE VIEWPORT, not to the step's
          content box.
          z-[35] sits strictly between the wizard's sticky step-indicator
          row (StickyBar's BELOW_LOGO_BAR_STICKY, z-30) and Layout's own
          logo bar (TOP_BAR_STICKY, z-40): tying z-30 with the sticky row
          made their stacking order fall to DOM position, which painted
          inconsistently as the page scrolled the backdrop's box through
          the pinned row (half-greyed strips). Sitting above it instead
          dims the wizard strip uniformly along with the step content,
          while staying below the actual logo bar so top nav stays clear.

          WHY FIXED AND NOT ABSOLUTE (2026-09-18/J). It was `absolute
          inset-0`, which sizes the backdrop to the STEP ROOT - and that
          root is `max-w-6xl mx-auto`, while the sticky header spans the
          full viewport width. The header was therefore dimmed only where
          the two boxes happened to OVERLAP, and its horizontal overhang
          was not: on the Answers step the buyer saw "Con" of "Continue
          Procurement" at the left edge and "349" of "PROC-000349" at the
          right edge at full contrast, with everything between them greyed.
          Fragments at both extremes, which is the signature of a width
          mismatch and not of a stacking context, a transform or a child
          z-index - none of which are involved.

          `fixed inset-0` also subsumes the reason the old comment gave for
          `absolute`: a content box taller than the viewport still can't be
          clicked through, because the backdrop now covers the viewport at
          every scroll position rather than travelling with the content. */}
      <div className="fixed inset-0 bg-white/60 z-[35]" data-testid="generation-lock-backdrop" />
      {/* Progress card - fixed to the viewport (not the backdrop above,
          which can be taller than the screen on long steps) so it's
          always centered on screen, no matter how far the user has
          scrolled. pointer-events-none on this wrapper so it doesn't
          swallow clicks on page chrome outside the step; the backdrop
          above already blocks the step content itself. */}
      <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-lg shadow-lg border border-zinc-200 px-8 py-6 flex flex-col items-center gap-3 text-center">
          <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
          <p className="text-sm font-medium text-zinc-700">{displayMessage}</p>
          {/* ELAPSED ON EVERY LOCK (Ashok, 2026-09-04). This used to render
              only when `stages` was passed, so the RFP lock on the Lines
              step showed "83s elapsed" while the refinement lock on the
              Review step - which now runs about as long on a 50-row BOQ -
              showed a static sentence and read as hung. The timer above
              always ran; only the line was gated. Generation calls now run
              for a minute rather than seconds, so every caller gets the
              counter, with no prop to opt out - a lock that hides how long
              it has been locked is the failure this closes. */}
          <p className="text-xs text-zinc-400 tabular-nums" data-testid="generation-lock-elapsed">{elapsedSeconds}s elapsed</p>
        </div>
      </div>
    </>
  );
};

export default GenerationLock;
